"use client";

import { c as _c } from "react/compiler-runtime";
import { Flex, FlexCenter, FlexEnd, Input, ItemAvatar, ItemContent, ItemTitle, Media, NumberDisplay, QuantityBadge, QuantityInput, SkeletonBlock, SkeletonLine, SpaceBetween, TABLE_SIZES, TableCell, TableRow, TextBody, TextOverflowTooltip, UnstyledButton, useTable } from "@opensea/ui-kit";
import { Close } from "@opensea/ui-kit/icons";
import { bn } from "@opensea/utils";
import { readFragment } from "gql.tada";
import { Controller, useFieldArray, useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";
import { Item } from "../../../../../components/Item";
import { RarityBadge } from "../../../../../components/RarityBadge";
import { useSyncFormFields } from "../../../../../hooks/useSyncFormField";
import { calculateOrderTotals } from "../../../../../lib/fees";
import { LISTING_MAX_DECIMALS, MINIMUM_LISTING_PRICE, ORDER_PRICE_HIGH_SEVERITY_WARNING_THRESHOLD_PERCENTAGE, ORDER_PRICE_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE } from "../../../constants";
import { LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES } from "./constants";
import { ListingFlowItemsTableRowFragment } from "./ListingFlowItemsTableRow.fragment";
import { readItemHighestTraitFloor } from "./readItemHighestTraitFloor";
export const ListingFlowItemsTableRow = t0 => {
  const $ = _c(132);
  const {
    item: itemKey,
    index,
    context
  } = t0;
  const t = useTranslations("wallet.ListingFlowItemsTableRow");
  const item = readFragment(ListingFlowItemsTableRowFragment, itemKey);
  const {
    register,
    control,
    setValue
  } = useFormContext();
  let t1;
  if ($[0] !== control) {
    t1 = {
      control
    };
    $[0] = control;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    errors
  } = useFormState(t1);
  let t2;
  if ($[2] !== control) {
    t2 = {
      keyName: "_id",
      control,
      name: "listings"
    };
    $[2] = control;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    remove
  } = useFieldArray(t2);
  const isReview = context?.isReview ?? false;
  let t3;
  if ($[4] !== control) {
    t3 = {
      control,
      name: "listings"
    };
    $[4] = control;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const listings = useWatch(t3);
  let t4;
  if ($[6] !== control) {
    t4 = {
      control,
      name: "adjustForFees"
    };
    $[6] = control;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const adjustForFees = useWatch(t4);
  const fieldItem = listings[index];
  const error = errors.listings?.[index]?.price;
  const quantityError = errors.listings?.[index]?.quantity;
  let t5;
  if ($[8] !== fieldItem) {
    t5 = calculateOrderTotals([fieldItem]);
    $[8] = fieldItem;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    adjustedForFees: totalAdjustedForFees,
    afterFees
  } = t5;
  const {
    size
  } = useTable();
  const imageSize = TABLE_SIZES[size].image;
  const isFungible = item?.isFungible;
  const floorPrice = isFungible ? item.bestListing?.pricePerItem.token : item?.collection?.floorPrice?.pricePerItem.token;
  const priceWarning = floorPrice && fieldItem.price > 0 && bn(fieldItem.price).lt(bn(floorPrice.unit).times(1 - ORDER_PRICE_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE).decimalPlaces(LISTING_MAX_DECIMALS)) ? t("priceBelowFloor", {
    price: bn(floorPrice.unit).decimalPlaces(LISTING_MAX_DECIMALS).toString(),
    symbol: floorPrice.symbol
  }) : undefined;
  const showHighSevPriceWarning = floorPrice && Boolean(priceWarning) && bn(fieldItem.price).lt(bn(floorPrice.unit).times(1 - ORDER_PRICE_HIGH_SEVERITY_WARNING_THRESHOLD_PERCENTAGE).decimalPlaces(LISTING_MAX_DECIMALS));
  let t6;
  if ($[10] !== quantityError || $[11] !== t) {
    t6 = quantityError && <TextBody color="error-1" size="xs">{quantityError.message || t("insufficientQuantity")}</TextBody>;
    $[10] = quantityError;
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const quantityErrorContent = t6;
  let t7;
  if ($[13] !== error || $[14] !== fieldItem.price || $[15] !== isReview || $[16] !== priceWarning || $[17] !== showHighSevPriceWarning) {
    t7 = fieldItem.price !== 0 && (priceWarning && !isReview || error) && <TextBody color={priceWarning && !showHighSevPriceWarning && !error ? "warning" : "error-1"} size="xs">{error ? error.message : priceWarning}</TextBody>;
    $[13] = error;
    $[14] = fieldItem.price;
    $[15] = isReview;
    $[16] = priceWarning;
    $[17] = showHighSevPriceWarning;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const priceErrorContent = t7;
  let t8;
  if ($[19] !== control || $[20] !== fieldItem.quantity || $[21] !== index || $[22] !== isFungible || $[23] !== isReview || $[24] !== quantityError) {
    t8 = isFungible ? isReview ? <QuantityBadge className="ml-1">{fieldItem.quantity}</QuantityBadge> : <Controller control={control} name={`listings.${index}.quantity`} render={t9 => {
      const {
        field
      } = t9;
      return <QuantityInput className="ml-1 min-w-[80px]" error={Boolean(quantityError)} min={0} onValueChange={field.onChange} size="sm" value={field.value} />;
    }} /> : null;
    $[19] = control;
    $[20] = fieldItem.quantity;
    $[21] = index;
    $[22] = isFungible;
    $[23] = isReview;
    $[24] = quantityError;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const quantityContent = t8;
  const t9 = `listings.${index}.shouldConfirmPrice`;
  let t10;
  if ($[26] !== showHighSevPriceWarning || $[27] !== t9) {
    t10 = [{
      name: t9,
      value: showHighSevPriceWarning
    }];
    $[26] = showHighSevPriceWarning;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== setValue || $[30] !== t10) {
    t11 = {
      fields: t10,
      setValue
    };
    $[29] = setValue;
    $[30] = t10;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  useSyncFormFields(t11);
  if (!item) {
    let t12;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <ListingFlowItemsTableRowSkeleton />;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    return t12;
  }
  const listingSymbol = item.chain.listingCurrency.symbol;
  let t12;
  if ($[33] !== imageSize || $[34] !== item.imageUrl) {
    t12 = <ItemAvatar className="rounded" size={imageSize} src={item.imageUrl} />;
    $[33] = imageSize;
    $[34] = item.imageUrl;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const t13 = item.name || `#${item.tokenId}`;
  let t14;
  if ($[36] !== t13) {
    t14 = <TextOverflowTooltip><ItemTitle>{t13}</ItemTitle></TextOverflowTooltip>;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== item) {
    t15 = <RarityBadge item={item} />;
    $[38] = item;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== quantityContent) {
    t16 = <Media greaterThanOrEqual="md">{quantityContent}</Media>;
    $[40] = quantityContent;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t14 || $[43] !== t15 || $[44] !== t16) {
    t17 = <ItemContent><FlexCenter className="w-full min-w-0 gap-2">{t14}{t15}{t16}</FlexCenter></ItemContent>;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t12 || $[47] !== t17) {
    t18 = <Item variant="unstyled">{t12}{t17}</Item>;
    $[46] = t12;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  const itemLockup = t18;
  let t19;
  if ($[49] !== error?.message || $[50] !== fieldItem.price || $[51] !== index || $[52] !== isReview || $[53] !== listingSymbol || $[54] !== priceWarning || $[55] !== register || $[56] !== showHighSevPriceWarning) {
    t19 = isReview ? <TextOverflowTooltip><TextBody><NumberDisplay suffix={listingSymbol} value={fieldItem.price} /></TextBody></TextOverflowTooltip> : <Input aria-label="Listing price" autoFocus={index === 0} className="font-mono" disabled={isReview} endEnhancer={listingSymbol} error={fieldItem.price !== 0 && (Boolean(error?.message) || showHighSevPriceWarning)} min={0} placeholder="0" size="sm" step={MINIMUM_LISTING_PRICE} type="number" warning={Boolean(priceWarning)} {...register(`listings.${index}.price`, {
      setValueAs: _temp
    })} />;
    $[49] = error?.message;
    $[50] = fieldItem.price;
    $[51] = index;
    $[52] = isReview;
    $[53] = listingSymbol;
    $[54] = priceWarning;
    $[55] = register;
    $[56] = showHighSevPriceWarning;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  const priceContent = t19;
  const showDeleteCell = listings.length > 1 && !isReview;
  let t20;
  if ($[58] !== index || $[59] !== remove) {
    t20 = () => {
      remove(index);
    };
    $[58] = index;
    $[59] = remove;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Close fill="interactive-primary-styles" size={20} />;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== t20) {
    t22 = <UnstyledButton onClick={t20}>{t21}</UnstyledButton>;
    $[62] = t20;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  const deleteButton = t22;
  let t23;
  if ($[64] !== itemLockup) {
    t23 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.item}>{itemLockup}</TableCell>;
    $[64] = itemLockup;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  const t24 = isFungible ? quantityContent : priceContent;
  let t25;
  if ($[66] !== t24) {
    t25 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.price}>{t24}</TableCell>;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== deleteButton || $[69] !== showDeleteCell) {
    t26 = showDeleteCell && <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.remove}>{deleteButton}</TableCell>;
    $[68] = deleteButton;
    $[69] = showDeleteCell;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== t23 || $[72] !== t25 || $[73] !== t26) {
    t27 = <TableRow>{t23}{t25}{t26}</TableRow>;
    $[71] = t23;
    $[72] = t25;
    $[73] = t26;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== isFungible || $[76] !== priceContent || $[77] !== showDeleteCell || $[78] !== t) {
    t28 = isFungible && <TableRow><TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.item}><TextBody weight="semibold">{t("itemPrice")}</TextBody></TableCell><TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.price}>{priceContent}</TableCell>{showDeleteCell && <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.remove} />}</TableRow>;
    $[75] = isFungible;
    $[76] = priceContent;
    $[77] = showDeleteCell;
    $[78] = t;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] !== priceErrorContent) {
    t29 = <FlexEnd>{priceErrorContent}</FlexEnd>;
    $[80] = priceErrorContent;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== quantityErrorContent || $[83] !== t27 || $[84] !== t28 || $[85] !== t29) {
    t30 = <Media lessThan="md">{t27}{quantityErrorContent}{t28}{t29}</Media>;
    $[82] = quantityErrorContent;
    $[83] = t27;
    $[84] = t28;
    $[85] = t29;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  if ($[87] !== itemLockup) {
    t31 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.item}>{itemLockup}</TableCell>;
    $[87] = itemLockup;
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  const t32 = floorPrice?.symbol;
  const t33 = floorPrice?.unit;
  let t34;
  if ($[89] !== t32 || $[90] !== t33) {
    t34 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.floor}><TextOverflowTooltip><TextBody><NumberDisplay suffix={t32} value={t33} /></TextBody></TextOverflowTooltip></TableCell>;
    $[89] = t32;
    $[90] = t33;
    $[91] = t34;
  } else {
    t34 = $[91];
  }
  let t35;
  if ($[92] !== item) {
    t35 = readItemHighestTraitFloor(item)?.token.symbol;
    $[92] = item;
    $[93] = t35;
  } else {
    t35 = $[93];
  }
  let t36;
  if ($[94] !== item) {
    t36 = readItemHighestTraitFloor(item)?.token.unit;
    $[94] = item;
    $[95] = t36;
  } else {
    t36 = $[95];
  }
  let t37;
  if ($[96] !== t35 || $[97] !== t36) {
    t37 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.traitFloor}><TextOverflowTooltip><TextBody><NumberDisplay suffix={t35} value={t36} /></TextBody></TextOverflowTooltip></TableCell>;
    $[96] = t35;
    $[97] = t36;
    $[98] = t37;
  } else {
    t37 = $[98];
  }
  const t38 = item.bestOffer?.pricePerItem.token.symbol;
  const t39 = item.bestOffer?.pricePerItem.token.unit;
  let t40;
  if ($[99] !== t38 || $[100] !== t39) {
    t40 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.topOffer}><TextOverflowTooltip><TextBody><NumberDisplay suffix={t38} value={t39} /></TextBody></TextOverflowTooltip></TableCell>;
    $[99] = t38;
    $[100] = t39;
    $[101] = t40;
  } else {
    t40 = $[101];
  }
  const t41 = item.lastSale?.token.symbol;
  const t42 = item.lastSale?.token.unit;
  let t43;
  if ($[102] !== t41 || $[103] !== t42) {
    t43 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.cost}><TextOverflowTooltip><TextBody><NumberDisplay suffix={t41} value={t42} /></TextBody></TextOverflowTooltip></TableCell>;
    $[102] = t41;
    $[103] = t42;
    $[104] = t43;
  } else {
    t43 = $[104];
  }
  const t44 = adjustForFees ? totalAdjustedForFees : afterFees;
  let t45;
  if ($[105] !== listingSymbol || $[106] !== t44) {
    t45 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.proceeds}><TextOverflowTooltip><TextBody><NumberDisplay suffix={listingSymbol} value={t44} /></TextBody></TextOverflowTooltip></TableCell>;
    $[105] = listingSymbol;
    $[106] = t44;
    $[107] = t45;
  } else {
    t45 = $[107];
  }
  let t46;
  if ($[108] !== priceContent) {
    t46 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.price}>{priceContent}</TableCell>;
    $[108] = priceContent;
    $[109] = t46;
  } else {
    t46 = $[109];
  }
  let t47;
  if ($[110] !== deleteButton || $[111] !== showDeleteCell) {
    t47 = showDeleteCell && <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.remove}>{deleteButton}</TableCell>;
    $[110] = deleteButton;
    $[111] = showDeleteCell;
    $[112] = t47;
  } else {
    t47 = $[112];
  }
  let t48;
  if ($[113] !== t31 || $[114] !== t34 || $[115] !== t37 || $[116] !== t40 || $[117] !== t43 || $[118] !== t45 || $[119] !== t46 || $[120] !== t47) {
    t48 = <TableRow>{t31}{t34}{t37}{t40}{t43}{t45}{t46}{t47}</TableRow>;
    $[113] = t31;
    $[114] = t34;
    $[115] = t37;
    $[116] = t40;
    $[117] = t43;
    $[118] = t45;
    $[119] = t46;
    $[120] = t47;
    $[121] = t48;
  } else {
    t48 = $[121];
  }
  let t49;
  if ($[122] !== priceErrorContent || $[123] !== quantityError || $[124] !== quantityErrorContent) {
    t49 = (Boolean(priceErrorContent) || quantityError) && <SpaceBetween className="w-full"><div>{quantityErrorContent}</div><div>{priceErrorContent}</div></SpaceBetween>;
    $[122] = priceErrorContent;
    $[123] = quantityError;
    $[124] = quantityErrorContent;
    $[125] = t49;
  } else {
    t49 = $[125];
  }
  let t50;
  if ($[126] !== t48 || $[127] !== t49) {
    t50 = <Media greaterThanOrEqual="md">{t48}{t49}</Media>;
    $[126] = t48;
    $[127] = t49;
    $[128] = t50;
  } else {
    t50 = $[128];
  }
  let t51;
  if ($[129] !== t30 || $[130] !== t50) {
    t51 = <>{t30}{t50}</>;
    $[129] = t30;
    $[130] = t50;
    $[131] = t51;
  } else {
    t51 = $[131];
  }
  return t51;
};
export const ListingFlowItemsTableRowSkeleton = context => {
  const $ = _c(17);
  const {
    size
  } = useTable();
  const imageSize = TABLE_SIZES[size].image;
  const numItems = context.context?.count ?? 1;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SkeletonBlock />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== imageSize) {
    t1 = <ItemAvatar className="rounded" size={imageSize}>{t0}</ItemAvatar>;
    $[1] = imageSize;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ItemContent><Flex className="gap-2"><SkeletonLine className="h-4 w-16" /></Flex></ItemContent>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.item}><Item variant="unstyled">{t1}{t2}</Item></TableCell>;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.floor}><SkeletonLine className="h-4 w-20" /></TableCell>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.traitFloor}><SkeletonLine className="h-4 w-20" /></TableCell>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.topOffer}><SkeletonLine className="h-4 w-20" /></TableCell>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.cost}><SkeletonLine className="h-4 w-20" /></TableCell>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.proceeds}><SkeletonLine className="h-4 w-20" /></TableCell>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.price}><SkeletonLine className="h-8 w-full" /></TableCell>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== numItems) {
    t10 = numItems > 1 && <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.remove}><SkeletonBlock className="size-5" /></TableCell>;
    $[12] = numItems;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t3) {
    t11 = <TableRow>{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}</TableRow>;
    $[14] = t10;
    $[15] = t3;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  return t11;
};
function _temp(value) {
  return value ? Number.parseFloat(value) : undefined;
}